import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';

interface BadgeSelectProps {
  value: string;
  options: { value: string; label: string; color?: string }[];
  onSelect: (value: string) => void | Promise<void>;
  className?: string;
}

export function BadgeSelect({ value, options, onSelect, className }: BadgeSelectProps) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; minWidth: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const menuHeight = Math.min(options.length * 32 + 8, 360);
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < menuHeight + 12 && rect.top > menuHeight + 12;
    setPos({
      top: openUp ? rect.top - menuHeight - 4 : rect.bottom + 4,
      left: rect.left,
      minWidth: Math.max(rect.width, 160),
    });
  }, [open, options.length]);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (
        triggerRef.current && !triggerRef.current.contains(t) &&
        menuRef.current && !menuRef.current.contains(t)
      ) {
        setOpen(false);
      }
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    function onScroll() {
      setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const current = options.find((o) => o.value === value);

  async function pick(v: string) {
    if (v === value) {
      setOpen(false);
      return;
    }
    setSaving(true);
    try {
      await onSelect(v);
    } finally {
      setSaving(false);
      setOpen(false);
    }
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        disabled={saving}
        className={cn(
          'inline-flex items-center gap-1 rounded-full border border-border/80 bg-card px-2 py-0.5 text-[11px] font-medium leading-tight transition-colors hover:bg-muted',
          current?.color,
          className,
        )}
      >
        <span className="lowercase">{current?.label ?? value}</span>
        <ChevronDown className="h-3 w-3 opacity-50" strokeWidth={2.25} />
      </button>
      {open && pos && createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            minWidth: pos.minWidth,
            zIndex: 9999,
          }}
          className="rounded-lg border border-border/80 bg-card p-1 shadow-lg shadow-foreground/5"
        >
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => pick(o.value)}
              className={cn(
                'flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors hover:bg-muted',
                o.value === value && 'bg-muted/60',
              )}
            >
              <span className={cn('inline-flex rounded-full px-1.5 py-0 text-[11px] leading-tight', o.color)}>
                {o.label}
              </span>
              {o.value === value && <span className="text-[10px] text-muted-foreground">atual</span>}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}
